import type { ApolloQueryResult } from '@apollo/client';
import { NetworkStatus, useQuery } from '@apollo/client';

import { EntityType } from '../../TraffickingPage/hooks/makePageDrawerPlugin';
import { getAdTerms, getLineItemTerms } from '../utils/utils';
import { GET_DRAWER_TARGETING_AD, GET_DRAWER_TARGETING_LINE_ITEM } from './queries';
import type { DrawerTargetingTableResult, QueryResult } from './types/results';

type HookProps = {
  id?: string;
  entityType: EntityType.AD | EntityType.LINE_ITEM;
};

type UseFetchTargetingResult = Omit<DrawerTargetingTableResult, 'submitForm' | 'isUpdating'> & {
  refetch: (variables?: { ids: string[] }) => Promise<ApolloQueryResult<QueryResult>>;
};

export const useFetchTargeting = ({ entityType, id }: HookProps): UseFetchTargetingResult => {
  const isAd = entityType === EntityType.AD;
  const query = isAd ? GET_DRAWER_TARGETING_AD : GET_DRAWER_TARGETING_LINE_ITEM;

  const { data, loading, networkStatus, refetch } = useQuery<QueryResult>(query, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { ids: [id] },
    // Without this, a post-save refetch() over already-cached data leaves `loading`
    // false and never re-renders, so the "Updating…" overlay never shows. Opting in
    // makes Apollo surface the in-flight refetch as a network-status change.
    notifyOnNetworkStatusChange: true,
  });

  const terms = isAd ? getAdTerms(data) : getLineItemTerms(data);

  return {
    ...terms,
    // `loading` covers the initial fetch; `NetworkStatus.refetch` covers a post-save
    // reconcile refetch where cached data keeps `loading` false.
    isFetching: loading || networkStatus === NetworkStatus.refetch,
    refetch,
  };
};
